import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Autocomplete,
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Select,
  Menu,
  MenuItem,
  FormControl,
  Skeleton,
  Alert,
  LinearProgress,
  Paper,
  Chip,
  Divider,
  Tabs,
  Tab,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  useMediaQuery,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  DirectionsCar as CarIcon,
  LocalGasStation as FuelIcon,
  Close as CloseIcon,
  DownloadRounded as DownloadRoundedIcon,
  People as PeopleIcon,
} from "@mui/icons-material";
import { resolveStatus } from "../../utils/vehicleStatus";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as ReTooltip,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

// ✅ APIs
import { useGetUserProfileQuery } from "../../redux/admin/userApi";
import { useGetAllLiteDevicesQuery } from "../../redux/admin/adminApi";
import { useGetTripsQuery } from "../../redux/logistics/logisticsApi";
import { useGetAlarmStatsQuery } from "../../redux/admin/alarmApi";
import { useGetAllUsersQuery, useGetDashboardSummaryQuery } from "../../redux/admin/dashboardApi";
import { useGetAllGroupsQuery } from "../../redux/admin/groupApi";
import {
  useGetRemindersQuery,
} from "../../redux/logistics/logisticsApi";
import { useListItemsQuery, useListTxnsQuery } from "../../redux/admin/inventoryApi";
import { useGetPendingTokenRequestsQuery } from "../../redux/admin/taskApi";

// ✅ RBAC
import {
  ROLES,
  MANAGEABLE_ROLES,
  getEffectivePermissions,
  getRoleDisplayName,
  normalizeRole,
} from "../../utils/rbacHelper";
import ScrollableTableContainer from "../common/ScrollableTableContainer";
import { useSocket } from "../../hooks/useSocket";
import { navigateToAdminDashboard } from "../../router/dashboardNavigation";

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────
const safeArr = (v) => (Array.isArray(v) ? v : []);
const toLower = (v) => String(v || "").toLowerCase();
const USER_PRESENCE_STALE_MS = Number(process.env.REACT_APP_USER_PRESENCE_STALE_MS || 45000);
const formatCount = (n) =>
  new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(Number(n || 0));
const formatDT = (d) => {
  try { return d ? new Date(d).toLocaleString() : "—"; } catch { return "—"; }
};
const pickRole = (profile) =>
  profile?.role || profile?.user?.role || profile?.data?.role || ROLES.VIEWER;
const daysBetween = (a, b) =>
  Math.floor((new Date(b).getTime() - new Date(a).getTime()) / (1000 * 60 * 60 * 24));
const pickExpiryDate = (lic) =>
  lic?.licenseRuntime?.expiryDate ||
  lic?.licenseRuntime?.licenseExpiryTime ||
  lic?.deviceLicense?.expiryDate ||
  lic?.deviceLicense?.licenseExpiryTime ||
  lic?.licenseExpiryTime ||
  lic?.licenseExpiryDate ||
  lic?.licenseExpiryTs ||
  lic?.expiryDate ||
  lic?.validTill ||
  lic?.expiresAt ||
  lic?.endDate ||
  lic?.toDate ||
  null;

const fmtDate = (d) => {
  const dt = new Date(d);
  return `${String(dt.getDate()).padStart(2, "0")}-${String(dt.getMonth() + 1).padStart(2, "0")}-${dt.getFullYear()}`;
};
const formatDistanceKm = (value) => Number(Number(value || 0).toFixed(2));
const getDeviceChartLabel = (device) =>
  String(device?.vehicleNumber || device?.name || device?.deviceId || "Unknown").trim() || "Unknown";
const resolveDeviceOdometerKm = (device) => {
  const candidates = [
    device?.odometer,
    device?.totalDistance,
    device?.totalDistanceRawKm,
    device?.liveData?.odometer,
    device?.liveData?.totalDistance,
    device?.currentLocation?.odometer,
    device?.currentLocation?.totalDistance,
    device?.totalOdo,
    device?.totalOdoRawKm,
    device?.mileage,
  ];

  for (const candidate of candidates) {
    const normalized = Number(candidate);
    if (Number.isFinite(normalized) && normalized >= 0) {
      return normalized;
    }
  }

  return 0;
};
const toTimeMs = (value) => {
  if (!value) return 0;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};
const formatRelativeTime = (value) => {
  const ts = toTimeMs(value);
  if (!ts) return "—";
  const diffMs = Math.max(0, Date.now() - ts);
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return formatDT(value);
};
const formatPresenceStatus = (value) => (value ? "Online" : "Offline");
const resolveUserPresenceOnline = (user, nowMs = Date.now()) => {
  if (!user?.isOnline) return false;
  const latestPresenceTs = Math.max(
    toTimeMs(user?.lastActivityAt),
    toTimeMs(user?.lastSeen),
    toTimeMs(user?.lastSeenAt)
  );
  if (!latestPresenceTs) return false;
  return nowMs - latestPresenceTs <= USER_PRESENCE_STALE_MS;
};
const getRoleLabel = (role) => getRoleDisplayName(role) || String(role || "User").replace(/_/g, " ");
const getRemainingLicenseCount = (user) =>
  Number(user?.licenseSummary?.totalRemainingCredits ?? user?.remainingTokens ?? 0) || 0;
const getPurchasedLicenseCount = (user) =>
  Number(user?.licenseSummary?.totalAssignedCount ?? 0) || 0;
const getUsedLicenseCount = (user) =>
  Math.max(0, getPurchasedLicenseCount(user) - getRemainingLicenseCount(user));
const getUserLastLogin = (user) => user?.lastLoginAt || user?.lastLogin || null;
const getUserLastActivity = (user) => user?.lastActivityAt || user?.updatedAt || user?.createdAt || null;
const getUserCreatedAt = (user) => user?.createdAt || user?.created_at || user?.createdOn || null;
const pickUserId = (profile) =>
  String(
    profile?._id ||
    profile?.id ||
    profile?.user?._id ||
    profile?.user?.id ||
    profile?.data?._id ||
    profile?.data?.id ||
    ""
  ).trim();
const resolveCreatorId = (user) =>
  String(
    user?.createdBy?._id ||
    user?.createdBy?.id ||
    user?.createdBy ||
    user?.createdById ||
    user?.ownerUser?._id ||
    user?.ownerUser?.id ||
    user?.ownerUser ||
    user?.dealerOwner?._id ||
    user?.dealerOwner?.id ||
    user?.dealerOwner ||
    user?.subAdminOwner?._id ||
    user?.subAdminOwner?.id ||
    user?.subAdminOwner ||
    ""
  ).trim();
const resolveCreatedByLabel = (user) => {
  const getLabel = (candidate) => {
    if (!candidate) return "";
    if (typeof candidate === "string") return String(candidate).trim();
    const username = String(candidate?.username || candidate?.name || "").trim();
    const uniqueId = String(candidate?.uniqueId || candidate?.userId || "").trim();
    if (username && uniqueId) return `${username} (${uniqueId})`;
    return username || uniqueId || "";
  };

  return (
    String(user?.createdByName || "").trim() ||
    getLabel(user?.createdBy) ||
    getLabel(user?.dealerOwner) ||
    getLabel(user?.subAdminOwner) ||
    "—"
  );
};
const API_BASE =
  process.env.REACT_APP_BACKEND_URL ||
  process.env.REACT_APP_API_URL ||
  "https://qylmox.in/api";
const ACTIVITY_ROWS_PER_PAGE = 8;
const ACTIVITY_POLL_INTERVAL_MS = 30000;
const ACTIVITY_FEED_ENDPOINTS = [
  "/dashboard/activity",
  "/notificationswebapp/user",
  "/notifications/user",
];

const getAuthToken = () => localStorage.getItem("token") || localStorage.getItem("authToken") || "";

const normalizeSystemNotification = (item = {}) => ({
  _id: item._id || item.notificationId || `system-${item.createdAt || Date.now()}`,
  title: item.title || "Notification",
  message: item.message || item.body || "",
  read: Boolean(item.read),
  type: item.eventType || item.type || "info",
  eventType: item.eventType || item.type || "info",
  source: item.source || "system",
  createdAt: item.createdAt || new Date().toISOString(),
  data: item.data || {},
});

const prettifyWords = (value) =>
  String(value || "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const pickFirstText = (...values) => {
  for (const value of values) {
    if (value === null || value === undefined) continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return "";
};

const getActivityEntityLabel = (notification = {}) => {
  const haystack = [
    notification.eventType,
    notification.type,
    notification.title,
    notification.message,
    notification.data?.entityType,
    notification.data?.module,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  if (haystack.includes("device") || haystack.includes("vehicle") || haystack.includes("imei")) return "Device";
  if (haystack.includes("driver")) return "Driver";
  if (haystack.includes("route")) return "Route";
  if (haystack.includes("task")) return "Task";
  if (haystack.includes("license") || haystack.includes("token")) return "License";
  if (haystack.includes("group")) return "Group";
  if (
    haystack.includes("user") ||
    haystack.includes("dealer") ||
    haystack.includes("admin") ||
    haystack.includes("client") ||
    haystack.includes("viewer") ||
    haystack.includes("employee") ||
    haystack.includes("manager")
  ) {
    return "User";
  }

  return "Update";
};

const getActivityActionLabel = (notification = {}) => {
  const haystack = [
    notification.eventType,
    notification.type,
    notification.title,
    notification.message,
    notification.data?.action,
    notification.data?.status,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  if (haystack.includes("delete") || haystack.includes("remove")) return "Deleted";
  if (haystack.includes("assign")) return "Assigned";
  if (haystack.includes("create") || haystack.includes("add") || haystack.includes("new ")) return "Created";
  if (haystack.includes("edit") || haystack.includes("update") || haystack.includes("modify")) return "Updated";
  if (haystack.includes("start")) return "Started";
  if (haystack.includes("complete")) return "Completed";
  if (haystack.includes("verify")) return "Verified";
  if (haystack.includes("share")) return "Shared";
  if (haystack.includes("approve")) return "Approved";
  if (haystack.includes("reject")) return "Rejected";
  return "Updated";
};

const getActivityTone = (actionLabel = "") => {
  const key = String(actionLabel || "").toLowerCase();
  if (key === "deleted" || key === "rejected") return "error";
  if (key === "created" || key === "approved" || key === "completed") return "success";
  if (key === "assigned" || key === "shared" || key === "started") return "info";
  return "warning";
};

const inferActivityRoleLabel = (notification = {}, actorName = "", actorRole = "") => {
  if (actorRole) return prettifyWords(actorRole);

  const eventType = String(notification?.eventType || notification?.type || "").toLowerCase();
  const data = notification?.data || {};
  if (eventType === "task_assigned" && data?.assignedBy && actorName !== "System") return "Office Employee";
  if (
    ["task_accepted", "task_rejected", "task_started", "task_completed", "task_verified"].includes(eventType) &&
    actorName !== "System"
  ) {
    return "Field Employee";
  }

  return "System";
};

const shouldShowActivityRow = (row = {}) => {
  const actorName = String(row?.actorName || "").trim().toLowerCase();
  const actorRole = String(row?.actorRole || "").trim().toLowerCase();
  return Boolean(
    row?.id &&
    (row?.summary || row?.entityName || row?.eventType) &&
    actorName &&
    actorName !== "system" &&
    actorRole &&
    actorRole !== "system"
  );
};

const normalizeActivityRow = (item = {}) => {
  const notification = normalizeSystemNotification(item);
  const data = notification.data || {};
  const actorName = pickFirstText(
    data.actorName,
    data.updatedByName,
    data.performedByName,
    data.createdByName,
    data.assignedBy,
    data.acceptedBy,
    data.rejectedBy,
    data.startedBy,
    data.completedBy,
    data.verifiedBy,
    data.username,
    data.userName,
    data.user?.username
  ) || "System";
  const actorRole = pickFirstText(data.actorRole, data.userRole, data.role, data.user?.role);
  const entityLabel = getActivityEntityLabel(notification);
  const actionLabel = getActivityActionLabel(notification);
  const entityName = pickFirstText(
    data.taskTitle,
    data.vehicleNumber,
    data.deviceName,
    data.vehicleName,
    data.driverName,
    data.routeName,
    data.groupName,
    data.entityName,
    data.targetName,
    data.name,
    data.imei,
    data.deviceId
  );

  return {
    id: String(notification._id),
    actorName,
    actorRole: inferActivityRoleLabel(notification, actorName, actorRole),
    entityLabel,
    actionLabel,
    tone: getActivityTone(actionLabel),
    entityName: entityName || "General update",
    summary: pickFirstText(notification.message, notification.title) || "Update received",
    eventType: notification.eventType || notification.type || "info",
    createdAt: notification.createdAt || new Date().toISOString(),
    relativeTime: formatRelativeTime(notification.createdAt),
    absoluteTime: formatDT(notification.createdAt),
    raw: notification,
  };
};
const getTripVehicleId = (trip) =>
  String(
    trip?.vehicle?._id ||
    trip?.vehicle?.id ||
    trip?.vehicleId?._id ||
    trip?.vehicleId ||
    trip?.device?._id ||
    trip?.device ||
    trip?.asset?._id ||
    trip?.asset ||
    ""
  ).trim();
const isOpenTrip = (trip) => {
  const tripStatus = String(trip?.tripStatus || "").trim().toLowerCase();
  const legacyStatus = String(trip?.status || "").trim().toLowerCase();
  const closed = new Set(["completed", "cancelled"]);
  if (tripStatus) return !closed.has(tripStatus);
  if (legacyStatus) return !closed.has(legacyStatus);
  return Boolean(trip?.startDate || trip?.routeName || trip?.vehicle);
};

// ─────────────────────────────────────────────
// Config
// ─────────────────────────────────────────────
const STATUS_CARD_CONFIGS = [
  { key: "all",         label: "All Vehicles",  bg: "#f97316" },
  { key: "running",     label: "Running",       bg: "#16a34a" },
  { key: "idling",      label: "Idling",        bg: "#ca8a04" },
  { key: "stopped",     label: "Stopped",       bg: "#dc2626" },
  { key: "towed",       label: "Towed",         bg: "#d81b60" },
  { key: "noReporting", label: "No Reporting",  bg: "#0ea5e9" },
  { key: "expired",     label: "Expired",       bg: "#7c3aed" },
  { key: "noData",      label: "No Data",       bg: "#6b7280" },
];

const FUEL_RATES = {
  "Andhra Pradesh": { diesel: 98.27, petrol: 110.48 },
  Delhi:            { diesel: 95.72, petrol: 105.41 },
  Maharashtra:      { diesel: 97.22, petrol: 111.18 },
  Gujarat:          { diesel: 96.44, petrol: 109.96 },
  Karnataka:        { diesel: 96.25, petrol: 110.22 },
  "Tamil Nadu":     { diesel: 99.12, petrol: 112.15 },
  "Uttar Pradesh":  { diesel: 95.01, petrol: 107.58 },
  Rajasthan:        { diesel: 98.94, petrol: 111.78 },
  "West Bengal":    { diesel: 97.68, petrol: 111.42 },
  Punjab:           { diesel: 95.12, petrol: 108.35 },
};

const PIE_COLORS = {
  running:     "#16a34a",
  idling:      "#ca8a04",
  stopped:     "#dc2626",
  towed:       "#d81b60",
  noReporting: "#0ea5e9",
  expired:     "#7c3aed",
  noData:      "#6b7280",
};

// ─────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────
function StatusCard({ label, value, bg, loading, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        bgcolor: bg,
        color: "white",
        borderRadius: 2,
        p: { xs: 1.25, sm: 1.75 },
        cursor: onClick ? "pointer" : "default",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.5,
        minWidth: { xs: 95, sm: 112 },
        flex: "1 1 0",
        transition: "transform 0.15s, opacity 0.15s",
        "&:hover": onClick ? { transform: "scale(1.04)", opacity: 0.88 } : {},
        boxShadow: "0 2px 8px rgba(0,0,0,0.22)",
      }}
    >
      {loading ? (
        <Skeleton variant="text" width={44} height={50} sx={{ bgcolor: alpha("#fff", 0.3) }} />
      ) : (
        <Typography variant="h4" sx={{ fontWeight: 900, lineHeight: 1, fontSize: { xs: 24, sm: 28 } }}>
          {value}
        </Typography>
      )}
      <CarIcon sx={{ fontSize: { xs: 22, sm: 26 }, opacity: 0.88 }} />
      <Typography
        sx={{ fontWeight: 700, textAlign: "center", fontSize: { xs: 10, sm: 11 }, lineHeight: 1.2, whiteSpace: "nowrap" }}
      >
        {label}
      </Typography>
    </Box>
  );
}

function ChartCard({ title, children, extraAction, noPad = false, contentRef = null }) {
  const [visible, setVisible] = useState(true);
  if (!visible) return null;
  return (
    <Paper elevation={2} sx={{ borderRadius: 2, overflow: "hidden", display: "flex", flexDirection: "column" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.25,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 800, fontSize: 13 }}>
          {title}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
          {extraAction}
          <IconButton size="small" onClick={() => setVisible(false)}>
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      </Box>
      <Box ref={contentRef} sx={{ p: noPad ? 0 : 2, flex: 1 }}>{children}</Box>
    </Paper>
  );
}

function EmptyState({ title, desc }) {
  return (
    <Box sx={{ py: 4, textAlign: "center" }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 950 }}>{title}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{desc}</Typography>
    </Box>
  );
}

const sanitizeExportName = (value) =>
  String(value || "dashboard-export")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "dashboard-export";

const triggerBlobDownload = (blob, fileName) => {
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(objectUrl);
};

const toExportCellValue = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "number") return value;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
};

const csvEscape = (value) => {
  const text = String(value ?? "");
  if (/[",\n]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
};

const mapRowsForExport = (columns, rows) =>
  safeArr(rows).map((row) =>
    columns.reduce((acc, column) => {
      const rawValue = typeof column.value === "function" ? column.value(row) : row?.[column.key];
      acc[column.label] = toExportCellValue(rawValue);
      return acc;
    }, {})
  );

const exportDatasetAsCsv = ({ fileName, columns, rows }) => {
  const mappedRows = mapRowsForExport(columns, rows);
  const header = columns.map((column) => csvEscape(column.label)).join(",");
  const body = mappedRows.map((row) => columns.map((column) => csvEscape(row[column.label])).join(","));
  const csv = [header, ...body].join("\n");
  triggerBlobDownload(new Blob([csv], { type: "text/csv;charset=utf-8;" }), `${sanitizeExportName(fileName)}.csv`);
};

const exportDatasetAsXlsx = ({ fileName, sheetName, columns, rows }) => {
  const mappedRows = mapRowsForExport(columns, rows);
  const worksheet = XLSX.utils.json_to_sheet(mappedRows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, (sheetName || "Sheet1").slice(0, 31));
  XLSX.writeFile(workbook, `${sanitizeExportName(fileName)}.xlsx`);
};

const exportDatasetAsPdf = ({ fileName, title, columns, rows }) => {
  const doc = new jsPDF({
    orientation: columns.length > 6 ? "landscape" : "portrait",
    unit: "pt",
    format: "a4",
  });

  doc.setFontSize(15);
  doc.text(title || "Dashboard Export", 40, 40);

  autoTable(doc, {
    startY: 56,
    head: [columns.map((column) => column.label)],
    body: safeArr(rows).map((row) =>
      columns.map((column) => {
        const rawValue = typeof column.value === "function" ? column.value(row) : row?.[column.key];
        return toExportCellValue(rawValue);
      })
    ),
    styles: {
      fontSize: 8,
      cellPadding: 4,
      overflow: "linebreak",
    },
    headStyles: {
      fillColor: [37, 99, 235],
      textColor: [255, 255, 255],
      fontStyle: "bold",
    },
    alternateRowStyles: {
      fillColor: [248, 250, 252],
    },
    margin: { top: 56, right: 40, bottom: 40, left: 40 },
  });

  doc.save(`${sanitizeExportName(fileName)}.pdf`);
};

const getSvgNodeFromContainer = (targetRef) => {
  const container = targetRef?.current || null;
  if (!container) return null;
  return container.tagName?.toLowerCase() === "svg" ? container : container.querySelector("svg");
};

const serializeSvgForDownload = (svgNode) => {
  if (!svgNode) return null;
  const clone = svgNode.cloneNode(true);
  const box = svgNode.getBoundingClientRect();
  const width = Math.max(1, Math.round(box.width || Number(svgNode.getAttribute("width")) || 800));
  const height = Math.max(1, Math.round(box.height || Number(svgNode.getAttribute("height")) || 400));

  clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  clone.setAttribute("xmlns:xlink", "http://www.w3.org/1999/xlink");
  clone.setAttribute("width", String(width));
  clone.setAttribute("height", String(height));
  clone.setAttribute("viewBox", clone.getAttribute("viewBox") || `0 0 ${width} ${height}`);

  return {
    width,
    height,
    markup: new XMLSerializer().serializeToString(clone),
  };
};

const exportChartAsSvg = ({ fileName, targetRef }) => {
  const serialized = serializeSvgForDownload(getSvgNodeFromContainer(targetRef));
  if (!serialized) return false;
  triggerBlobDownload(
    new Blob([serialized.markup], { type: "image/svg+xml;charset=utf-8" }),
    `${sanitizeExportName(fileName)}.svg`
  );
  return true;
};

const exportChartAsPng = async ({ fileName, targetRef }) => {
  const serialized = serializeSvgForDownload(getSvgNodeFromContainer(targetRef));
  if (!serialized) return false;

  const encoded = window.btoa(unescape(encodeURIComponent(serialized.markup)));
  const image = new Image();
  image.decoding = "async";
  image.src = `data:image/svg+xml;base64,${encoded}`;

  await new Promise((resolve, reject) => {
    image.onload = resolve;
    image.onerror = reject;
  });

  const canvas = document.createElement("canvas");
  canvas.width = serialized.width * 2;
  canvas.height = serialized.height * 2;
  const context = canvas.getContext("2d");
  if (!context) return false;

  context.setTransform(2, 0, 0, 2, 0, 0);
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, serialized.width, serialized.height);
  context.drawImage(image, 0, 0, serialized.width, serialized.height);

  const link = document.createElement("a");
  link.href = canvas.toDataURL("image/png");
  link.download = `${sanitizeExportName(fileName)}.png`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  return true;
};

// ─────────────────────────────────────────────
// Dashboard
// ─────────────────────────────────────────────
export default function Dashboard() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));
  const { socket } = useSocket();
  const authUser = useSelector((state) => state.auth?.user);
  const summaryScope = String(authUser?._id || authUser?.id || "anonymous");

  const [searchVehicleInput, setSearchVehicleInput] = useState("");
  const [selectedVehicleId, setSelectedVehicleId] = useState("");
  const timeframe = "7d";
  const [fuelState, setFuelState] = useState("Andhra Pradesh");
  const [roleTab, setRoleTab] = useState("");
  const [activityRows, setActivityRows] = useState([]);
  const [activityTotal, setActivityTotal] = useState(0);
  const [activityLoading, setActivityLoading] = useState(false);
  const [secondaryQueriesEnabled, setSecondaryQueriesEnabled] = useState(false);
  const distanceChartRef = useRef(null);
  const topOdometerChartRef = useRef(null);
  const tripsChartRef = useRef(null);
  const vehicleStatusChartRef = useRef(null);
  const directoryRoleChartRef = useRef(null);
  const licenseRoleChartRef = useRef(null);

  // ── Profile ──
  const { data: profile, isLoading: profileLoading, refetch: refetchProfile } =
    useGetUserProfileQuery();
  const role = pickRole(profile);
  const permissions = useMemo(
    () => getEffectivePermissions(profile?.user || profile?.data || profile || role),
    [profile, role]
  );
  const currentUserId = useMemo(() => pickUserId(profile), [profile]);
  const isSuper = role === ROLES.SUPER_ADMIN;
  const isEmployee =
    role === ROLES.OFFICE_EMPLOYEE || role === ROLES.FIELD_EMPLOYEE || role === ROLES.HR;
  const canSeeUsers = Boolean(permissions.viewUsers);
  const [presenceNowMs, setPresenceNowMs] = useState(() => Date.now());

  useEffect(() => {
    const timerId = window.setInterval(() => {
      setPresenceNowMs(Date.now());
    }, 10000);
    return () => window.clearInterval(timerId);
  }, []);

  // ── Devices ──
  const {
    data: dashboardSummary,
    isLoading: summaryLoading,
    refetch: refetchDashboardSummary,
  } = useGetDashboardSummaryQuery(
    { scope: summaryScope, refresh: true },
    { skip: !permissions.viewDevices }
  );

  useEffect(() => {
    if (secondaryQueriesEnabled) return undefined;
    if (profileLoading) return undefined;
    if (permissions.viewDevices && summaryLoading) return undefined;

    const timerId = window.setTimeout(() => {
      setSecondaryQueriesEnabled(true);
    }, 0);

    return () => window.clearTimeout(timerId);
  }, [permissions.viewDevices, profileLoading, secondaryQueriesEnabled, summaryLoading]);

  const { data: devicesRaw, isLoading: devicesLoading, refetch: refetchDevices } =
    useGetAllLiteDevicesQuery(
      { includeRelations: "true" },
      { skip: !permissions.viewDevices || !secondaryQueriesEnabled }
    );
  const devices = useMemo(() => safeArr(devicesRaw), [devicesRaw]);

  // ── Trips ──
  const { data: tripsRaw, isLoading: tripsLoading, refetch: refetchTrips } =
    useGetTripsQuery(undefined, { skip: !permissions.viewDashboard || !secondaryQueriesEnabled });
  const trips = useMemo(() => safeArr(tripsRaw), [tripsRaw]);

  // ── Alarms ──
  const { refetch: refetchAlarms } =
    useGetAlarmStatsQuery(undefined, { skip: !permissions.viewEvents });

  // ── Users / directory ──
  const {
    data: allUsersRaw,
    isLoading: usersLoading,
    isUninitialized: usersQueryUninitialized,
    refetch: refetchScopedUsers,
  } = useGetAllUsersQuery(undefined, { skip: !canSeeUsers || !secondaryQueriesEnabled });
  const scopedUsers = useMemo(() => safeArr(allUsersRaw), [allUsersRaw]);

  const manageableRoles = useMemo(() => new Set(MANAGEABLE_ROLES[role] || []), [role]);
  const roleUserTables = useMemo(() => {
    const canSeeRole = (tgt) => isSuper || manageableRoles.has(tgt) || role === tgt;
    return [
      { role: ROLES.MANAGER,         label: "Manager" },
      { role: ROLES.HR,              label: "HR" },
      { role: ROLES.OFFICE_EMPLOYEE, label: "Office Employee" },
      { role: ROLES.FIELD_EMPLOYEE,  label: "Field Employee" },
      { role: ROLES.SUB_ADMIN,       label: "Admin", superAdminOnly: true },
      { role: ROLES.DEALER,          label: "Dealer" },
      { role: ROLES.CLIENT,          label: "Client" },
      { role: ROLES.VIEWER,          label: "Driver" },
      { role: ROLES.USER,            label: "User" },
    ]
      .filter(
        (b) =>
          (!b.superAdminOnly || isSuper) &&
          canSeeRole(b.role) &&
          !(role === ROLES.DEALER && b.role === ROLES.DEALER)
      )
      .map((b) => ({
        ...b,
        rows: scopedUsers
          .filter((user) => {
            const userRole = normalizeRole(user?.role);
            if (userRole !== b.role) return false;

            if (b.role !== ROLES.VIEWER) return true;
            if (!currentUserId) return true;

            const creatorId = resolveCreatorId(user);
            const userId = String(user?._id || user?.id || "").trim();

            if (role === ROLES.VIEWER) {
              return userId === currentUserId;
            }

            return creatorId === currentUserId;
          })
        .map((u, idx) => ({
          key: `${b.role}-${u?._id || idx}`,
          name: u?.name || u?.username || "—",
          role: u?.role || b.role,
          roleLabel: getRoleLabel(u?.role || b.role),
          mobile: u?.mobileNo || u?.phone || u?.mobile || "—",
          isOnline: resolveUserPresenceOnline(u, presenceNowMs),
          lastSeenAt: u?.lastSeen || null,
          createdByName: resolveCreatedByLabel(u),
          createdAt: getUserCreatedAt(u),
          purchasedLicenses: getPurchasedLicenseCount(u),
          usedLicenses: getUsedLicenseCount(u),
          remainingLicenses: getRemainingLicenseCount(u),
          lastLoginAt: getUserLastLogin(u),
          lastActivityAt: getUserLastActivity(u),
        }))
          .sort((left, right) => {
            const activityDiff = toTimeMs(right.lastActivityAt) - toTimeMs(left.lastActivityAt);
            if (activityDiff !== 0) return activityDiff;
            return toTimeMs(right.lastLoginAt) - toTimeMs(left.lastLoginAt);
          }),
      }));
  }, [currentUserId, isSuper, manageableRoles, presenceNowMs, role, scopedUsers]);

  const activeRoleTab = useMemo(() => {
    if (!roleUserTables.length) return "";
    return roleUserTables.some((s) => s.role === roleTab) ? roleTab : roleUserTables[0].role;
  }, [roleTab, roleUserTables]);
  const activeRoleSection = useMemo(
    () => roleUserTables.find((s) => s.role === activeRoleTab) || null,
    [roleUserTables, activeRoleTab]
  );

  const activeDirectoryRows = useMemo(() => activeRoleSection?.rows || [], [activeRoleSection]);
  const directoryHighlights = useMemo(() => {
    const latestLoginRow = [...activeDirectoryRows].sort((a, b) => toTimeMs(b.lastLoginAt) - toTimeMs(a.lastLoginAt))[0] || null;
    const latestActivityRow = [...activeDirectoryRows].sort((a, b) => toTimeMs(b.lastActivityAt) - toTimeMs(a.lastActivityAt))[0] || null;

    return {
      visibleUsers: activeDirectoryRows.length,
      purchasedLicenses: activeDirectoryRows.reduce((sum, row) => sum + Number(row.purchasedLicenses || 0), 0),
      usedLicenses: activeDirectoryRows.reduce((sum, row) => sum + Number(row.usedLicenses || 0), 0),
      remainingLicenses: activeDirectoryRows.reduce((sum, row) => sum + Number(row.remainingLicenses || 0), 0),
      latestLoginRow,
      latestActivityRow,
    };
  }, [activeDirectoryRows]);
  const roleOverviewChartData = useMemo(
    () =>
      roleUserTables.map((section) => ({
        role: section.label,
        users: section.rows.length,
        purchasedLicenses: section.rows.reduce((sum, row) => sum + Number(row.purchasedLicenses || 0), 0),
        usedLicenses: section.rows.reduce((sum, row) => sum + Number(row.usedLicenses || 0), 0),
        remainingLicenses: section.rows.reduce((sum, row) => sum + Number(row.remainingLicenses || 0), 0),
      })),
    [roleUserTables]
  );
  const licenseTableRows = useMemo(
    () =>
      [...scopedUsers]
        .map((user) => ({
          id: String(user?._id || ""),
          name: user?.name || user?.username || "—",
          roleLabel: getRoleLabel(user?.role),
          isOnline: resolveUserPresenceOnline(user, presenceNowMs),
          lastSeenAt: user?.lastSeen || null,
          createdByName: resolveCreatedByLabel(user),
          createdAt: getUserCreatedAt(user),
          purchasedLicenses: getPurchasedLicenseCount(user),
          usedLicenses: getUsedLicenseCount(user),
          remainingLicenses: getRemainingLicenseCount(user),
          lastActivityAt: getUserLastActivity(user),
          lastLoginAt: getUserLastLogin(user),
        }))
        .sort((left, right) => {
          const purchasedDiff = Number(right.purchasedLicenses || 0) - Number(left.purchasedLicenses || 0);
          if (purchasedDiff !== 0) return purchasedDiff;
          return toTimeMs(right.lastActivityAt) - toTimeMs(left.lastActivityAt);
        }),
    [presenceNowMs, scopedUsers]
  );
  const days = timeframe === "30d" ? 30 : timeframe === "14d" ? 14 : 7;

  // ── Super Admin extras ──
  const { refetch: refetchGroups }      = useGetAllGroupsQuery({ page: 1, limit: 20 }, { skip: !isSuper || !secondaryQueriesEnabled });
  const { refetch: refetchInv }         = useListItemsQuery({}, { skip: !isSuper || !secondaryQueriesEnabled });
  const { data: invTxnsRaw, refetch: refetchInvTxns } =
    useListTxnsQuery({ limit: 8 }, { skip: !isSuper || !secondaryQueriesEnabled });
  const invTxns = useMemo(() => safeArr(invTxnsRaw?.data || invTxnsRaw), [invTxnsRaw]);
  const { refetch: refetchPendingTokens } = useGetPendingTokenRequestsQuery(undefined, { skip: !isSuper || !secondaryQueriesEnabled });
  const { refetch: refetchReminders } = useGetRemindersQuery(undefined, { skip: !isSuper || !secondaryQueriesEnabled });

  const vehicleOptions = useMemo(
    () =>
      devices
        .map((device) => {
          const id = String(device?._id || device?.id || "").trim();
          const vehicleNumber = String(device?.vehicleNumber || "").trim();
          const name = String(device?.name || device?.deviceName || "").trim();
          const imei = String(device?.imei || device?.deviceId || "").trim();
          const label = vehicleNumber || name || imei || id || "Unknown Vehicle";

          return {
            id,
            label,
            vehicleNumber,
            name,
            imei,
            searchText: [label, vehicleNumber, name, imei].filter(Boolean).join(" ").toLowerCase(),
          };
        })
        .filter((option) => option.id),
    [devices]
  );

  const selectedVehicleOption = useMemo(
    () => vehicleOptions.find((option) => option.id === selectedVehicleId) || null,
    [selectedVehicleId, vehicleOptions]
  );

  useEffect(() => {
    if (selectedVehicleId && !vehicleOptions.some((option) => option.id === selectedVehicleId)) {
      setSelectedVehicleId("");
    }
  }, [selectedVehicleId, vehicleOptions]);

  useEffect(() => {
    const query = String(searchVehicleInput || "").trim().toLowerCase();
    if (!query) return;

    const exact = vehicleOptions.find((option) =>
      [option.label, option.vehicleNumber, option.name, option.imei]
        .filter(Boolean)
        .some((value) => String(value).trim().toLowerCase() === query)
    );

    if (exact && exact.id !== selectedVehicleId) {
      setSelectedVehicleId(exact.id);
    }
  }, [searchVehicleInput, selectedVehicleId, vehicleOptions]);

  // ── Vehicle stats (dashboard summary first, lite devices fallback) ──
  const filteredDevices = useMemo(
    () =>
      selectedVehicleId
        ? devices.filter((d) => String(d?._id || d?.id || "").trim() === selectedVehicleId)
        : devices,
    [devices, selectedVehicleId]
  );
  const scopedDevices = filteredDevices;
  const scopedDeviceIdSet = useMemo(
    () => new Set(scopedDevices.map((device) => String(device?._id || device?.id || "").trim()).filter(Boolean)),
    [scopedDevices]
  );

  const derivedVehicleStats = useMemo(() => {
    const s = { all: scopedDevices.length, running: 0, idling: 0, stopped: 0, towed: 0, noReporting: 0, expired: 0, noData: 0 };
    scopedDevices.forEach((d) => {
      const st = toLower(resolveStatus(d));
      if (st === "running" || st === "moving")                        s.running++;
      else if (st === "idling" || st === "idle")                      s.idling++;
      else if (st === "stopped" || st === "parked")                   s.stopped++;
      else if (st === "towed")                                         s.towed++;
      else if (st === "no-reporting" || st === "offline" || st === "inactive") s.noReporting++;
      else if (st === "expired")                                       s.expired++;
      else                                                             s.noData++;
    });
    return s;
  }, [scopedDevices]);
  const vehicleStats = useMemo(() => {
    if (selectedVehicleId) return derivedVehicleStats;
    const counts = dashboardSummary?.counts || {};
    const summaryTotal = Number(counts.total || 0);
    if (!summaryTotal) return derivedVehicleStats;

    return {
      all: summaryTotal,
      running: Number(counts.running || 0),
      idling: Number(counts.idle || 0),
      stopped: Number(counts.stopped || 0),
      towed: Number(counts.towed || 0),
      noReporting: Number(counts.inactive || 0),
      expired: Number(counts.expired || 0),
      noData: Number(counts.no_data || 0),
    };
  }, [selectedVehicleId, dashboardSummary?.counts, derivedVehicleStats]);

  const scopedTrips = useMemo(() => {
    if (!selectedVehicleId) return trips;
    return trips.filter((trip) => scopedDeviceIdSet.has(getTripVehicleId(trip)));
  }, [selectedVehicleId, scopedDeviceIdSet, trips]);

  // ── Trip status (On Trip / No Trip) ──
  const tripStatus = useMemo(() => {
    const vehiclesWithTrips = new Set();

    scopedTrips.forEach((trip) => {
      if (!isOpenTrip(trip)) return;
      const vehicleId = getTripVehicleId(trip);
      if (!vehicleId) return;
      if (scopedDeviceIdSet.size === 0 || scopedDeviceIdSet.has(vehicleId)) {
        vehiclesWithTrips.add(vehicleId);
      }
    });

    const onTrip = vehiclesWithTrips.size;
    const fleetTotal = vehicleStats.all || scopedDevices.length;
    const noTrip = Math.max(0, fleetTotal - onTrip);
    return { onTrip, noTrip };
  }, [scopedDeviceIdSet, scopedDevices.length, scopedTrips, vehicleStats.all]);

  // ── Distance chart (last N days with real dates) ──
  const distanceData = useMemo(() => {
    const out = [];
    for (let i = days - 1; i >= 0; i--) {
      const dt = new Date();
      dt.setDate(dt.getDate() - i);
      const total = i === 0
        ? scopedDevices.reduce((sum, d) => sum + Number(d?.dailyDistance || 0), 0)
        : 0;
      out.push({ date: fmtDate(dt), distance: Math.round(total) });
    }
    return out;
  }, [scopedDevices, days]);

  // ── Top 10 odometer ──
  const topOdometerDevices = useMemo(
    () =>
      [...scopedDevices]
        .map((device) => ({
          label: getDeviceChartLabel(device),
          odometer: formatDistanceKm(resolveDeviceOdometerKm(device)),
        }))
        .sort((left, right) => right.odometer - left.odometer || left.label.localeCompare(right.label))
        .slice(0, 10),
    [scopedDevices]
  );
  const topOdometerChartData = useMemo(() => [...topOdometerDevices].reverse(), [topOdometerDevices]);
  const topOdometerMax = useMemo(
    () => topOdometerDevices.reduce((max, row) => Math.max(max, Number(row.odometer || 0)), 0),
    [topOdometerDevices]
  );
  const topOdometerChartHeight = useMemo(
    () => Math.max(320, topOdometerChartData.length * 34),
    [topOdometerChartData.length]
  );

  // ── Trips chart (last N days with real dates) ──
  const tripsDateData = useMemo(() => {
    const out = [];
    for (let i = days - 1; i >= 0; i--) {
      const dt = new Date();
      dt.setDate(dt.getDate() - i);
      const dayTrips = scopedTrips.filter((t) => {
        const when = new Date(t?.startDate || t?.createdAt || t?.date || 0);
        return when.toDateString() === dt.toDateString();
      }).length;
      out.push({ date: fmtDate(dt), trips: dayTrips });
    }
    return out;
  }, [scopedTrips, days]);

  // ── Pie chart data for vehicle status ──
  const vehiclePieData = useMemo(() =>
    [
      { name: "Running",      value: vehicleStats.running,     color: PIE_COLORS.running },
      { name: "Idling",       value: vehicleStats.idling,      color: PIE_COLORS.idling },
      { name: "Stopped",      value: vehicleStats.stopped,     color: PIE_COLORS.stopped },
      { name: "Towed",        value: vehicleStats.towed,       color: PIE_COLORS.towed },
      { name: "No Reporting", value: vehicleStats.noReporting, color: PIE_COLORS.noReporting },
      { name: "Expired",      value: vehicleStats.expired,     color: PIE_COLORS.expired },
      { name: "No Data",      value: vehicleStats.noData,      color: PIE_COLORS.noData },
    ].filter((d) => d.value > 0),
    [vehicleStats]
  );

  // ── License KPIs (Super Admin) ──
  const licenseKpis = useMemo(() => {
    if (!isSuper) return { expiringSoon: 0, expired: 0, topExpiring: [], expiringList: [] };

    const sourceDevices = (selectedVehicleId ? scopedDevices : devices).filter(Boolean);
    const today = new Date();

    const withExp = sourceDevices
      .map((device) => {
        const expiry = pickExpiryDate(device);
        if (!expiry) return null;

        return {
          id: String(device?._id || device?.id || device?.imei || device?.name || Math.random()),
          title: device?.vehicleNumber || device?.name || device?.imei || "Unknown Device",
          vehicleNumber: device?.vehicleNumber || device?.name || "—",
          imei: device?.imei || "—",
          __exp: expiry,
        };
      })
      .filter(Boolean)
      .filter((device) => !Number.isNaN(new Date(device.__exp).getTime()));

    const expired = withExp.filter((device) => new Date(device.__exp) < today).length;
    const expiringSoonList = withExp
      .map((device) => ({ ...device, __days: daysBetween(today, device.__exp) }))
      .filter((device) => device.__days >= 0 && device.__days <= 30)
      .sort((a, b) => a.__days - b.__days || new Date(a.__exp).getTime() - new Date(b.__exp).getTime());

    return {
      expiringSoon: expiringSoonList.length,
      expired,
      expiringList: expiringSoonList,
      topExpiring: expiringSoonList.slice(0, 6),
    };
  }, [devices, isSuper, scopedDevices, selectedVehicleId]);

  const fuelRates = FUEL_RATES[fuelState] || FUEL_RATES["Andhra Pradesh"];
  const anyLoading = profileLoading || summaryLoading || devicesLoading || tripsLoading || usersLoading;
  const [activityPage, setActivityPage] = useState(0);
  const [exportMenuAnchorEl, setExportMenuAnchorEl] = useState(null);
  const [activeExportConfig, setActiveExportConfig] = useState(null);

  const fetchActivityFeed = useCallback(async (pageOverride = activityPage, options = {}) => {
    if (!canSeeUsers && !permissions.viewDevices && !permissions.viewDashboard) {
      setActivityRows([]);
      setActivityTotal(0);
      return;
    }

    const token = getAuthToken();
    if (!token) {
      setActivityRows([]);
      setActivityTotal(0);
      return;
    }

    try {
      if (!options.silent) setActivityLoading(true);
      let notifications = [];
      let total = 0;
      let lastError = null;
      const requestedPage = Math.max(0, Number(pageOverride) || 0);

      for (const endpoint of ACTIVITY_FEED_ENDPOINTS) {
        try {
          const searchParams = new URLSearchParams({
            page: String(requestedPage + 1),
            limit: String(ACTIVITY_ROWS_PER_PAGE),
          });

          const response = await fetch(`${API_BASE}${endpoint}?${searchParams.toString()}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (!response.ok) {
            throw new Error(`Failed to load updates from ${endpoint} (${response.status})`);
          }

          const payload = await response.json();
          if (payload?.success === false) {
            throw new Error(payload?.msg || payload?.error || `Failed to load updates from ${endpoint}`);
          }
          const nextNotifications = Array.isArray(payload)
            ? payload
            : Array.isArray(payload?.notifications)
              ? payload.notifications
              : Array.isArray(payload?.data)
                ? payload.data
                : [];
          const nextTotal = Number(
            payload?.total ??
            payload?.count ??
            nextNotifications.length
          );

          notifications = nextNotifications;
          total = Number.isFinite(nextTotal) ? nextTotal : nextNotifications.length;
          break;
        } catch (error) {
          lastError = error;
        }
      }

      if (!notifications.length && lastError) {
        console.warn("Dashboard activity feed fallback used with empty result:", lastError);
      }

      const rows = notifications
        .map(normalizeActivityRow)
        .filter(shouldShowActivityRow)
        .sort((left, right) => toTimeMs(right.createdAt) - toTimeMs(left.createdAt));

      setActivityRows(rows);
      setActivityTotal(Number.isFinite(total) && total > 0 ? total : rows.length);
    } catch (error) {
      console.error("Failed to load dashboard activity feed:", error);
    } finally {
      if (!options.silent) setActivityLoading(false);
    }
  }, [activityPage, canSeeUsers, permissions.viewDashboard, permissions.viewDevices]);

  useEffect(() => {
    if (!secondaryQueriesEnabled) return;
    fetchActivityFeed(activityPage);
  }, [activityPage, fetchActivityFeed, secondaryQueriesEnabled]);

  useEffect(() => {
    if (!secondaryQueriesEnabled) return undefined;
    if (!socket) return undefined;

    const handleNotification = () => {
      fetchActivityFeed(activityPage, { silent: true });
    };

    socket.on("notification", handleNotification);
    return () => {
      socket.off("notification", handleNotification);
    };
  }, [activityPage, fetchActivityFeed, secondaryQueriesEnabled, socket]);

  useEffect(() => {
    if (!socket || !canSeeUsers || !secondaryQueriesEnabled || usersQueryUninitialized) return undefined;

    const handleUserPresence = () => {
      try {
        refetchScopedUsers();
      } catch (error) {
        console.warn("Skipped scoped users refetch before query initialization:", error?.message || error);
      }
    };

    socket.on("user_presence", handleUserPresence);
    return () => {
      socket.off("user_presence", handleUserPresence);
    };
  }, [canSeeUsers, refetchScopedUsers, secondaryQueriesEnabled, socket, usersQueryUninitialized]);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(activityTotal / ACTIVITY_ROWS_PER_PAGE) - 1);
    setActivityPage((prev) => Math.min(prev, maxPage));
  }, [activityTotal]);

  useEffect(() => {
    if (!secondaryQueriesEnabled) return undefined;
    const timerId = window.setInterval(() => {
      fetchActivityFeed(activityPage, { silent: true });
    }, ACTIVITY_POLL_INTERVAL_MS);

    return () => window.clearInterval(timerId);
  }, [activityPage, fetchActivityFeed, secondaryQueriesEnabled]);

  const topOdometerExportConfig = useMemo(
    () => ({
      title: "Top 10 Odometer",
      fileName: "dashboard-top-10-odometer",
      sheetName: "Top 10 Odometer",
      formats: ["svg", "png", "csv"],
      targetRef: topOdometerChartRef,
      columns: [
        { key: "label", label: "Vehicle" },
        { key: "odometer", label: "Distance (Kms)" },
      ],
      rows: topOdometerDevices,
    }),
    [topOdometerDevices]
  );

  const distanceExportConfig = useMemo(
    () => ({
      title: `Last ${days} Days Distance`,
      fileName: "dashboard-last-days-distance",
      sheetName: "Distance Trend",
      formats: ["svg", "png", "csv"],
      targetRef: distanceChartRef,
      columns: [
        { key: "date", label: "Date" },
        { key: "distance", label: "Distance (Kms)" },
      ],
      rows: distanceData,
    }),
    [days, distanceData]
  );

  const tripsExportConfig = useMemo(
    () => ({
      title: `Last ${days} Days Trips`,
      fileName: "dashboard-last-days-trips",
      sheetName: "Trips Trend",
      formats: ["svg", "png", "csv"],
      targetRef: tripsChartRef,
      columns: [
        { key: "date", label: "Date" },
        { key: "trips", label: "Trips Count" },
      ],
      rows: tripsDateData,
    }),
    [days, tripsDateData]
  );

  const vehicleStatusExportConfig = useMemo(
    () => ({
      title: "Vehicle Status Distribution",
      fileName: "dashboard-vehicle-status-distribution",
      sheetName: "Vehicle Status Distribution",
      formats: ["svg", "png", "csv"],
      targetRef: vehicleStatusChartRef,
      columns: [
        { key: "name", label: "Status" },
        { key: "value", label: "Vehicles" },
      ],
      rows: vehiclePieData,
    }),
    [vehiclePieData]
  );

  const roleDistributionExportConfig = useMemo(
    () => ({
      title: "Role Distribution",
      fileName: "dashboard-role-distribution",
      sheetName: "Role Distribution",
      formats: ["svg", "png", "csv"],
      targetRef: directoryRoleChartRef,
      columns: [
        { key: "role", label: "Role" },
        { key: "users", label: "Users" },
      ],
      rows: roleOverviewChartData,
    }),
    [roleOverviewChartData]
  );

  const licenseRoleExportConfig = useMemo(
    () => ({
      title: "Purchased vs Remaining by Role",
      fileName: "dashboard-license-role-overview",
      sheetName: "License Role Overview",
      formats: ["svg", "png", "csv"],
      targetRef: licenseRoleChartRef,
      columns: [
        { key: "role", label: "Role" },
        { key: "purchasedLicenses", label: "Purchased Licenses" },
        { key: "usedLicenses", label: "Used Licenses" },
        { key: "remainingLicenses", label: "Remaining Licenses" },
      ],
      rows: roleOverviewChartData,
    }),
    [roleOverviewChartData]
  );

  const latestUpdatesExportConfig = useMemo(
    () => ({
      title: "Latest Updates",
      fileName: "dashboard-latest-updates",
      sheetName: "Latest Updates",
      columns: [
        { key: "actorName", label: "Username" },
        { key: "actorRole", label: "Role" },
        { label: "Update Type", value: (row) => `${row.actionLabel} ${row.entityLabel}`.trim() },
        { key: "entityName", label: "Updated Item" },
        { key: "summary", label: "Details" },
        { key: "absoluteTime", label: "Date & Time" },
      ],
      rows: activityRows,
    }),
    [activityRows]
  );

  const directoryExportConfig = useMemo(
    () => ({
      title: `${activeRoleSection?.label || "Organization"} Directory`,
      fileName: `dashboard-${activeRoleSection?.label || "organization"}-directory`,
      sheetName: `${activeRoleSection?.label || "Directory"} Users`,
      columns: [
        { key: "name", label: "Name" },
        { key: "roleLabel", label: "Role" },
        { key: "mobile", label: "Mobile" },
        { label: "Status", value: (row) => formatPresenceStatus(row.isOnline) },
        { key: "createdByName", label: "Created By" },
        { key: "purchasedLicenses", label: "Purchased" },
        { key: "usedLicenses", label: "Used" },
        { key: "remainingLicenses", label: "Remaining" },
        { label: "Last Login", value: (row) => formatDT(row.lastLoginAt) },
        { label: "Last Activity", value: (row) => formatDT(row.lastActivityAt) },
      ],
      rows: activeDirectoryRows,
    }),
    [activeDirectoryRows, activeRoleSection?.label]
  );

  const licenseOverviewExportConfig = useMemo(
    () => ({
      title: "License Overview",
      fileName: "dashboard-license-overview",
      sheetName: "License Overview",
      columns: [
        { key: "name", label: "User" },
        { key: "roleLabel", label: "Role" },
        { key: "createdByName", label: "Created By" },
        { label: "Status", value: (row) => formatPresenceStatus(row.isOnline) },
        { label: "Last Seen", value: (row) => formatDT(row.lastSeenAt) },
        { label: "Created At", value: (row) => formatDT(row.createdAt) },
        { key: "purchasedLicenses", label: "Purchased" },
        { key: "usedLicenses", label: "Used" },
        { key: "remainingLicenses", label: "Remaining" },
        { label: "Last Login", value: (row) => formatDT(row.lastLoginAt) },
        { label: "Last Activity", value: (row) => formatDT(row.lastActivityAt) },
      ],
      rows: licenseTableRows,
    }),
    [licenseTableRows]
  );

  const openExportMenu = useCallback((event, config) => {
    setExportMenuAnchorEl(event.currentTarget);
    setActiveExportConfig(config);
  }, []);

  const closeExportMenu = useCallback(() => {
    setExportMenuAnchorEl(null);
    setActiveExportConfig(null);
  }, []);

  const handleExportSelection = useCallback(
    async (format) => {
      if (!activeExportConfig) return;

      if (format === "svg") {
        exportChartAsSvg(activeExportConfig);
        closeExportMenu();
        return;
      }

      if (format === "png") {
        try {
          await exportChartAsPng(activeExportConfig);
        } catch (error) {
          console.error("Failed to export chart as PNG:", error);
        }
        closeExportMenu();
        return;
      }

      if (format === "csv") exportDatasetAsCsv(activeExportConfig);
      if (format === "xlsx") exportDatasetAsXlsx(activeExportConfig);
      if (format === "pdf") exportDatasetAsPdf(activeExportConfig);
      closeExportMenu();
    },
    [activeExportConfig, closeExportMenu]
  );

  const renderExportTrigger = useCallback(
    (config) => (
        <Tooltip title="Download">
          <IconButton size="small" onClick={(event) => openExportMenu(event, config)}>
            <DownloadRoundedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
    ),
    [openExportMenu]
  );

  const handleRefresh = () => {
    refetchProfile();
    if (permissions.viewDevices) refetchDashboardSummary();
    if (permissions.viewDevices) refetchDevices();
    if (permissions.viewDashboard) refetchTrips();
    if (permissions.viewEvents) refetchAlarms();
    if (canSeeUsers) refetchScopedUsers();
    if (isSuper) {
      refetchGroups(); refetchInv(); refetchInvTxns();
      refetchPendingTokens(); refetchReminders();
    }
    fetchActivityFeed(activityPage);
  };

  const redirectToMapStatus = (status = "all") => {
    const statusMap = {
      idling: "idle",
      noReporting: "inactive",
      noData: "no_data",
    };
    navigateToAdminDashboard(navigate, {
      tab: "map",
      mapStatusFilter: statusMap[status] || status,
    });
  };

  // ─────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────
  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f4f6f8" }}>
      {anyLoading && <LinearProgress />}

      {/* ══ 1. STATUS CARDS ROW ══ */}
      {permissions.viewDevices && role !== ROLES.FIELD_EMPLOYEE && (
        <Box
          sx={{
            bgcolor: "white",
            px: { xs: 1, sm: 2 },
            py: 1.5,
            boxShadow: "0 1px 6px rgba(0,0,0,0.10)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: { xs: 0.75, sm: 1.25 },
              overflowX: "auto",
              pb: 0.5,
              "&::-webkit-scrollbar": { height: 4 },
              "&::-webkit-scrollbar-thumb": { bgcolor: "#ccc", borderRadius: 4 },
            }}
          >
            {STATUS_CARD_CONFIGS.map((card) => (
              <StatusCard
                key={card.key}
                label={card.label}
                value={vehicleStats[card.key]}
                bg={card.bg}
                loading={summaryLoading}
                onClick={() => redirectToMapStatus(card.key)}
              />
            ))}
          </Box>
          {dashboardSummary?.generatedAt ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5, px: 0.5 }}>
              Updated {formatRelativeTime(dashboardSummary.generatedAt)}
            </Typography>
          ) : null}
        </Box>
      )}

      {/* ══ 2. SEARCH ROW ══ */}
      {permissions.viewDevices && (
        <Box
          sx={{
            bgcolor: "white",
            px: { xs: 1, sm: 2 },
            py: 1.25,
            mt: 0.75,
            boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
          }}
        >
          <Autocomplete
            fullWidth
            options={vehicleOptions}
            value={selectedVehicleOption}
            inputValue={searchVehicleInput}
            autoHighlight
            clearOnEscape
            handleHomeEndKeys
            openOnFocus
            selectOnFocus
            noOptionsText={searchVehicleInput ? "No matching vehicle found" : "Type to search vehicles"}
            onInputChange={(_event, value, reason) => {
              setSearchVehicleInput(value);
              if (reason === "clear") {
                setSelectedVehicleId("");
                return;
              }
              if (reason === "input" && selectedVehicleId) {
                setSelectedVehicleId("");
              }
            }}
            onChange={(_event, option) => {
              setSelectedVehicleId(option?.id || "");
              setSearchVehicleInput(option?.label || "");
            }}
            getOptionLabel={(option) => (typeof option === "string" ? option : option?.label || "")}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            filterOptions={(options, state) => {
              const query = String(state.inputValue || "").trim().toLowerCase();
              if (!query) return options.slice(0, 20);
              return options.filter((option) => option.searchText.includes(query)).slice(0, 20);
            }}
            renderOption={(props, option) => {
              const { key, ...optionProps } = props;
              return (
                <li key={key} {...optionProps}>
                  <Box sx={{ display: "flex", flexDirection: "column", py: 0.25 }}>
                    <Typography variant="body2" sx={{ fontWeight: 800, color: "#132238" }}>
                      {option.vehicleNumber || option.label}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      {[option.name, option.imei].filter(Boolean).join(" • ") || option.label}
                    </Typography>
                  </Box>
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search Vehicle"
                placeholder="Type vehicle number, name, or IMEI"
                size="small"
                helperText={
                  selectedVehicleOption
                    ? `Showing dashboard for ${selectedVehicleOption.label}`
                    : "Search and select the vehicle whose dashboard data you want to see"
                }
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" color="action" />
                      </InputAdornment>
                      {params.InputProps?.startAdornment}
                    </>
                  ),
                  endAdornment: (
                    <>
                      {selectedVehicleOption ? (
                        <Tooltip title="Clear selection">
                          <IconButton
                            onClick={() => {
                              setSelectedVehicleId("");
                              setSearchVehicleInput("");
                            }}
                            size="small"
                            sx={{ color: "error.main", mr: 0.5 }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                      <Tooltip title="Refresh">
                        <IconButton onClick={handleRefresh} size="small" sx={{ color: "primary.main", mr: 0.5 }}>
                          <RefreshIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {params.InputProps?.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          {selectedVehicleOption && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
              Selected vehicle: {selectedVehicleOption.label} • Showing dashboard data for this vehicle
            </Typography>
          )}
        </Box>
      )}

      <Box sx={{ px: { xs: 1, sm: 2 }, pb: 2 }}>
        <Paper elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderBottom: "1px solid",
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                Latest Updates
              </Typography>
              <Typography variant="caption" color="text.secondary">
                The latest role-based activity appears here in real time, with the newest updates at the top.
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Chip
                size="small"
                color="success"
                variant="outlined"
                label="Realtime"
                sx={{ fontWeight: 800 }}
              />
              <Chip
                size="small"
                color={activityTotal ? "primary" : "default"}
                label={`${activityTotal} updates`}
                sx={{ fontWeight: 800 }}
              />
              {renderExportTrigger(latestUpdatesExportConfig)}
            </Box>
          </Box>

          <Box sx={{ p: 2 }}>
            {activityLoading ? (
              <Skeleton variant="rounded" height={260} />
            ) : activityRows.length ? (
              <>
                <ScrollableTableContainer
                  sx={(t) => ({
                    border: `1px solid ${alpha(t.palette.divider, 0.7)}`,
                    borderRadius: 2,
                    overflow: "hidden",
                    "& table": { minWidth: 980 },
                  })}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={(t) => ({ bgcolor: alpha(t.palette.primary.main, 0.06) })}>
                        <TableCell sx={{ fontWeight: 900 }}>Username</TableCell>
                        <TableCell sx={{ fontWeight: 900 }}>Role</TableCell>
                        <TableCell sx={{ fontWeight: 900 }}>Update Type</TableCell>
                        <TableCell sx={{ fontWeight: 900 }}>Updated Item</TableCell>
                        <TableCell sx={{ fontWeight: 900 }}>Details</TableCell>
                        <TableCell sx={{ fontWeight: 900 }}>Date & Time</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {activityRows.map((row) => (
                        <TableRow
                          key={row.id}
                          hover
                          sx={(t) => ({
                            bgcolor:
                              Date.now() - toTimeMs(row.createdAt) <= 2 * 60 * 1000
                                ? alpha(t.palette.success.main, 0.04)
                                : "transparent",
                          })}
                        >
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 800 }}>
                              {row.actorName}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              variant="outlined"
                              label={row.actorRole}
                              sx={{ fontWeight: 700 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                              <Chip
                                size="small"
                                color={row.tone}
                                label={row.actionLabel}
                                sx={{ fontWeight: 800 }}
                              />
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {row.entityLabel}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {row.entityName}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ minWidth: 260 }}>
                            <Typography variant="body2" color="text.secondary">
                              {row.summary}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {row.relativeTime}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {row.absoluteTime}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollableTableContainer>
                <TablePagination
                  component="div"
                  count={activityTotal}
                  page={activityPage}
                  onPageChange={(_, nextPage) => setActivityPage(nextPage)}
                  rowsPerPage={ACTIVITY_ROWS_PER_PAGE}
                  rowsPerPageOptions={[ACTIVITY_ROWS_PER_PAGE]}
                  labelRowsPerPage="Rows per page"
                  labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
                  sx={{
                    mt: 1,
                    borderTop: "1px solid",
                    borderColor: "divider",
                  }}
                />
              </>
            ) : (
              <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
                No recent update activity is available right now.
              </Alert>
            )}
          </Box>
        </Paper>
      </Box>

      {/* ══ 3. MAIN CHARTS — ROW 1 (Distance + Odometer + Trip Status / Fuel) ══ */}
      {permissions.viewDashboard && !isEmployee && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "1fr 1fr",
              lg: "1fr 1fr 320px",
            },
            gap: 2,
            p: { xs: 1, sm: 2 },
          }}
        >
          {/* Last N Days Distance — Line Chart */}
          <ChartCard title={`Last ${days} Days Distance`} extraAction={renderExportTrigger(distanceExportConfig)}>
            {devicesLoading ? (
              <Skeleton variant="rounded" height={300} />
            ) : (
              <>
                <Box ref={distanceChartRef} sx={{ width: "100%", height: 260 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={distanceData}
                      margin={{ top: 8, right: 12, left: -14, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#efefef" />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 10 }}
                        interval="preserveStartEnd"
                      />
                      <YAxis
                        tick={{ fontSize: 10 }}
                        label={{
                          value: "Distance (Kms)",
                          angle: -90,
                          position: "insideLeft",
                          offset: 14,
                          style: { fontSize: 11, fill: "#888" },
                        }}
                      />
                      <ReTooltip />
                      <Line
                        type="monotone"
                        dataKey="distance"
                        stroke="#1565c0"
                        strokeWidth={2}
                        dot={{ r: 3, fill: "#1565c0" }}
                        name="Distance (Kms)"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", textAlign: "center", mt: 0.5, fontWeight: 700 }}
                >
                  Date
                </Typography>
              </>
            )}
          </ChartCard>

          {/* Top 10 Odometer — Horizontal Bar Chart */}
          <ChartCard
            title="Top 10 Odometer"
            extraAction={renderExportTrigger(topOdometerExportConfig)}
          >
            {devicesLoading ? (
              <Skeleton variant="rounded" height={300} />
            ) : topOdometerChartData.length ? (
              <>
                <Box ref={topOdometerChartRef} sx={{ width: "100%", height: topOdometerChartHeight }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={topOdometerChartData}
                      layout="vertical"
                      margin={{ top: 8, right: 28, left: isMdDown ? 72 : 116, bottom: 10 }}
                      barCategoryGap={8}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#efefef" />
                      <XAxis
                        type="number"
                        tick={{ fontSize: 10 }}
                        domain={[0, topOdometerMax > 0 ? "dataMax + 1" : 1]}
                        label={{
                          value: "Distance (Kms)",
                          position: "insideBottom",
                          offset: -2,
                          style: { fontSize: 11, fill: "#888" },
                        }}
                      />
                      <YAxis
                        type="category"
                        dataKey="label"
                        tick={{ fontSize: 10 }}
                        width={isMdDown ? 72 : 116}
                        label={{
                          value: "Devices",
                          angle: -90,
                          position: "insideLeft",
                          offset: isMdDown ? 70 : 106,
                          style: { fontSize: 11, fill: "#888" },
                        }}
                      />
                      <ReTooltip formatter={(value) => [`${formatDistanceKm(value)} km`, "Distance"]} />
                      <Bar
                        dataKey="odometer"
                        fill="#1565c0"
                        radius={[0, 4, 4, 0]}
                        name="Odometer (km)"
                        barSize={18}
                        label={{
                          position: "right",
                          fill: "#132238",
                          fontSize: 10,
                          formatter: (value) => formatDistanceKm(value),
                        }}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", textAlign: "center", mt: 0.5, fontWeight: 700 }}
                >
                  Distance (Kms)
                </Typography>
              </>
            ) : (
              <EmptyState title="No odometer data" desc="Vehicles have no odometer readings yet." />
            )}
          </ChartCard>

          {/* Right column: Trip Status + Fuel Rate */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Vehicle Trip Status */}
            <ChartCard title="Vehicle Trip Status">
              {tripsLoading ? (
                <Skeleton variant="rounded" height={100} />
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-around",
                    alignItems: "center",
                    py: 2,
                  }}
                >
                  <Box sx={{ textAlign: "center" }}>
                    <Typography
                      variant="h3"
                      sx={{ fontWeight: 900, lineHeight: 1, color: "text.primary" }}
                    >
                      {tripStatus.onTrip}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                      On Trip
                    </Typography>
                  </Box>
                  <Divider orientation="vertical" flexItem />
                  <Box sx={{ textAlign: "center" }}>
                    <Typography
                      variant="h3"
                      sx={{ fontWeight: 900, lineHeight: 1, color: "text.primary" }}
                    >
                      {tripStatus.noTrip}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                      No Trip
                    </Typography>
                  </Box>
                </Box>
              )}
            </ChartCard>

            {/* Today's Fuel Rate */}
            <ChartCard title="Today's Fuel Rate">
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <FormControl fullWidth size="small">
                  <Select
                    value={fuelState}
                    onChange={(e) => setFuelState(e.target.value)}
                    sx={{ borderRadius: 1.5 }}
                  >
                    {Object.keys(FUEL_RATES).map((state) => (
                      <MenuItem key={state} value={state}>
                        {state}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Divider />

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-around",
                    alignItems: "center",
                    py: 0.5,
                  }}
                >
                  <Box sx={{ textAlign: "center" }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                      Diesel
                    </Typography>
                    <FuelIcon sx={{ fontSize: 40, color: "#e53935", my: 0.5 }} />
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      INR {fuelRates.diesel}
                    </Typography>
                  </Box>
                  <Divider orientation="vertical" flexItem />
                  <Box sx={{ textAlign: "center" }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                      Petrol
                    </Typography>
                    <FuelIcon sx={{ fontSize: 40, color: "#e53935", my: 0.5 }} />
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      INR {fuelRates.petrol}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </ChartCard>
          </Box>
        </Box>
      )}

      {/* ══ 4. CHARTS ROW 2 — Trips + Vehicle Status Pie ══ */}
      {permissions.viewDashboard && !isEmployee && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 2,
            px: { xs: 1, sm: 2 },
            pb: 2,
          }}
        >
          {/* Last N Days Trips — Line Chart */}
          <ChartCard title={`Last ${days} Days Trips`} extraAction={renderExportTrigger(tripsExportConfig)}>
            {tripsLoading ? (
              <Skeleton variant="rounded" height={300} />
            ) : (
              <>
                <Box ref={tripsChartRef} sx={{ width: "100%", height: 260 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={tripsDateData}
                      margin={{ top: 8, right: 12, left: -14, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#efefef" />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 10 }}
                        interval="preserveStartEnd"
                      />
                      <YAxis
                        tick={{ fontSize: 10 }}
                        allowDecimals={false}
                        label={{
                          value: "Trips Count",
                          angle: -90,
                          position: "insideLeft",
                          offset: 14,
                          style: { fontSize: 11, fill: "#888" },
                        }}
                      />
                      <ReTooltip />
                      <Line
                        type="monotone"
                        dataKey="trips"
                        stroke="#1565c0"
                        strokeWidth={2}
                        dot={{ r: 3, fill: "#1565c0" }}
                        name="Trips Count"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", textAlign: "center", mt: 0.5, fontWeight: 700 }}
                >
                  Date
                </Typography>
              </>
            )}
          </ChartCard>

          {/* Vehicle Status Distribution — Donut Pie Chart */}
          {permissions.viewDevices && (
            <ChartCard title="Vehicle Status Distribution" extraAction={renderExportTrigger(vehicleStatusExportConfig)}>
              {devicesLoading ? (
                <Skeleton variant="rounded" height={300} />
              ) : vehiclePieData.length ? (
                <Box ref={vehicleStatusChartRef} sx={{ width: "100%", height: 280 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={vehiclePieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={55}
                        outerRadius={90}
                        paddingAngle={3}
                        dataKey="value"
                      >
                        {vehiclePieData.map((entry, idx) => (
                          <Cell key={`cell-${idx}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <ReTooltip formatter={(val, name) => [val, name]} />
                      <Legend
                        formatter={(value) => (
                          <span style={{ fontSize: 12 }}>{value}</span>
                        )}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <EmptyState title="No vehicle data" desc="All vehicles have equal or no status data." />
              )}
            </ChartCard>
          )}
        </Box>
      )}

      {/* ══ 5. ORGANIZATION DIRECTORY (Sub Admin, Dealer) ══ */}
      {canSeeUsers && roleUserTables.length > 0 && (
        <Box sx={{ px: { xs: 1, sm: 2 }, pb: 2 }}>
          <Paper elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderBottom: "1px solid",
                borderColor: "divider",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                bgcolor: "background.paper",
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                Organization Directory
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Chip
                  size="small"
                  icon={<PeopleIcon />}
                  label={isSuper ? "Global" : "Scoped"}
                  variant="outlined"
                  color={isSuper ? "primary" : "default"}
                />
                {renderExportTrigger(directoryExportConfig)}
              </Box>
            </Box>

            <Box sx={{ borderBottom: "1px solid", borderColor: "divider", px: 1 }}>
              <Tabs
                value={activeRoleTab}
                onChange={(_, v) => setRoleTab(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  minHeight: 40,
                  "& .MuiTab-root": {
                    minHeight: 40,
                    textTransform: "none",
                    fontWeight: 800,
                    fontSize: 13,
                  },
                }}
              >
                {roleUserTables.map((section) => (
                  <Tab
                    key={section.role}
                    value={section.role}
                    label={`${section.label} (${section.rows.length})`}
                  />
                ))}
              </Tabs>
            </Box>

            <Box sx={{ p: 2 }}>
              {activeRoleSection ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", xl: "1.3fr 1fr" },
                      gap: 2,
                    }}
                  >
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
                        gap: 1.25,
                      }}
                    >
                      {[
                        { label: "Visible Users", value: formatCount(directoryHighlights.visibleUsers), tone: "primary" },
                        { label: "Purchased Licenses", value: formatCount(directoryHighlights.purchasedLicenses), tone: "warning" },
                        { label: "Used Licenses", value: formatCount(directoryHighlights.usedLicenses), tone: "success" },
                        { label: "Remaining Licenses", value: formatCount(directoryHighlights.remainingLicenses), tone: "info" },
                      ].map((item) => (
                        <Paper
                          key={item.label}
                          variant="outlined"
                          sx={(t) => ({
                            p: 1.5,
                            borderRadius: 2,
                            bgcolor: alpha(t.palette[item.tone].main, 0.06),
                            borderColor: alpha(t.palette[item.tone].main, 0.16),
                          })}
                        >
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                            {item.label}
                          </Typography>
                          <Typography sx={{ mt: 0.5, fontSize: 22, lineHeight: 1.1, fontWeight: 900 }}>
                            {item.value}
                          </Typography>
                        </Paper>
                      ))}
                    </Box>

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                        gap: 1.25,
                      }}
                    >
                      {[
                        {
                          label: "Latest Login",
                          row: directoryHighlights.latestLoginRow,
                          field: "lastLoginAt",
                          color: "success.main",
                        },
                        {
                          label: "Latest Activity",
                          row: directoryHighlights.latestActivityRow,
                          field: "lastActivityAt",
                          color: "warning.main",
                        },
                      ].map((item) => (
                        <Paper key={item.label} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                            {item.label}
                          </Typography>
                          <Typography sx={{ mt: 0.6, fontWeight: 900 }}>
                            {item.row?.name || "—"}
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.35, color: item.color, fontWeight: 800 }}>
                            {item.row ? formatRelativeTime(item.row[item.field]) : "—"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.35 }}>
                            {item.row ? formatDT(item.row[item.field]) : "No record available"}
                          </Typography>
                        </Paper>
                      ))}
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", lg: "340px 1fr" },
                      gap: 2,
                    }}
                  >
                    <Paper variant="outlined" sx={{ borderRadius: 2, p: 1.5 }}>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 900 }}>
                          Role Distribution
                        </Typography>
                        {renderExportTrigger(roleDistributionExportConfig)}
                      </Box>
                      {roleOverviewChartData.length ? (
                        <Box ref={directoryRoleChartRef} sx={{ width: "100%", height: 220 }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={roleOverviewChartData} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#efefef" />
                              <XAxis dataKey="role" tick={{ fontSize: 10 }} interval={0} angle={-18} textAnchor="end" height={52} />
                              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                              <ReTooltip />
                              <Bar dataKey="users" fill="#2563eb" radius={[6, 6, 0, 0]} name="Users" />
                            </BarChart>
                          </ResponsiveContainer>
                        </Box>
                      ) : (
                        <EmptyState title="No users found" desc="No visible users available for graph." />
                      )}
                    </Paper>

                    <ScrollableTableContainer
                      showScrollButtons={false}
                      sx={(t) => ({
                        border: `1px solid ${alpha(t.palette.divider, 0.7)}`,
                        borderRadius: 2,
                        overflow: "hidden",
                        "& table": { minWidth: 920 },
                      })}
                    >
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={(t) => ({ bgcolor: alpha(t.palette.primary.main, 0.06) })}>
                            <TableCell sx={{ fontWeight: 900 }}>User</TableCell>
                            <TableCell sx={{ fontWeight: 900 }}>Role</TableCell>
                            <TableCell sx={{ fontWeight: 900 }}>Mobile</TableCell>
                            <TableCell sx={{ fontWeight: 900 }}>Created By</TableCell>
                            <TableCell sx={{ fontWeight: 900 }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: 900 }}>Last Seen</TableCell>
                            <TableCell sx={{ fontWeight: 900 }}>Created At</TableCell>
                            <TableCell sx={{ fontWeight: 900 }} align="right">Purchased Licenses</TableCell>
                            <TableCell sx={{ fontWeight: 900 }} align="right">Used Licenses</TableCell>
                            <TableCell sx={{ fontWeight: 900 }} align="right">Remaining Licenses</TableCell>
                            <TableCell sx={{ fontWeight: 900 }}>Last Login</TableCell>
                            <TableCell sx={{ fontWeight: 900 }}>Last Activity</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {activeRoleSection.rows.slice(0, 18).map((row, idx) => (
                            <TableRow
                              key={row.key}
                              hover
                              sx={(t) => ({
                                bgcolor: idx % 2 ? alpha(t.palette.action.hover, 0.2) : "transparent",
                              })}
                            >
                              <TableCell sx={{ fontWeight: 800 }}>{row.name}</TableCell>
                              <TableCell>
                                <Chip size="small" label={row.roleLabel} variant="outlined" />
                              </TableCell>
                              <TableCell sx={{ fontFamily: "monospace" }}>{row.mobile}</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>{row.createdByName}</TableCell>
                              <TableCell sx={{ fontWeight: 800, color: row.isOnline ? "#16a34a" : "#6b7280" }}>
                                {formatPresenceStatus(row.isOnline)}
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                  {formatRelativeTime(row.lastSeenAt)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {formatDT(row.lastSeenAt)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                  {formatRelativeTime(row.createdAt)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {formatDT(row.createdAt)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 800 }}>{formatCount(row.purchasedLicenses)}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 800 }}>{formatCount(row.usedLicenses)}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 800, color: "primary.main" }}>{formatCount(row.remainingLicenses)}</TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                  {formatRelativeTime(row.lastLoginAt)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {formatDT(row.lastLoginAt)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                  {formatRelativeTime(row.lastActivityAt)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {formatDT(row.lastActivityAt)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                          {!activeRoleSection.rows.length && (
                            <TableRow>
                              <TableCell colSpan={12}>
                                <Typography variant="body2" color="text.secondary">
                                  No users in this role.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </ScrollableTableContainer>
                  </Box>
                </Box>
              ) : (
                <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
                  No users available in your visible scope.
                </Alert>
              )}
            </Box>
          </Paper>
        </Box>
      )}

      {/* ══ 6. LICENSE OVERVIEW ══ */}
      {canSeeUsers && licenseTableRows.length > 0 && (
        <Box sx={{ px: { xs: 1, sm: 2 }, pb: 2 }}>
          <Paper elevation={2} sx={{ borderRadius: 2 }}>
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderBottom: "1px solid",
                borderColor: "divider",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                License Overview
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                {renderExportTrigger(licenseOverviewExportConfig)}
                <Tooltip title="Refresh">
                  <IconButton size="small" onClick={refetchScopedUsers}>
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            <Box sx={{ p: 2, display: "grid", gridTemplateColumns: { xs: "1fr", xl: "1.15fr 1fr" }, gap: 2 }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(3, 1fr)" },
                    gap: 1,
                  }}
                >
                  {[
                    { label: "Purchased Licenses", value: formatCount(licenseTableRows.reduce((sum, row) => sum + Number(row.purchasedLicenses || 0), 0)) },
                    { label: "Used Licenses", value: formatCount(licenseTableRows.reduce((sum, row) => sum + Number(row.usedLicenses || 0), 0)) },
                    { label: "Remaining Licenses", value: formatCount(licenseTableRows.reduce((sum, row) => sum + Number(row.remainingLicenses || 0), 0)) },
                  ].map((item) => (
                    <Paper key={item.label} variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                        {item.label}
                      </Typography>
                      <Typography sx={{ mt: 0.5, fontWeight: 900, fontSize: 20 }}>
                        {item.value}
                      </Typography>
                    </Paper>
                  ))}
                </Box>
                <Paper variant="outlined" sx={{ borderRadius: 2, p: 1.5 }}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 900 }}>
                      Purchased vs Remaining by Role
                    </Typography>
                    {renderExportTrigger(licenseRoleExportConfig)}
                  </Box>
                  {roleOverviewChartData.length ? (
                    <Box ref={licenseRoleChartRef} sx={{ width: "100%", height: 220 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={roleOverviewChartData} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#efefef" />
                          <XAxis dataKey="role" tick={{ fontSize: 10 }} interval={0} angle={-18} textAnchor="end" height={52} />
                          <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                          <ReTooltip />
                          <Legend />
                          <Bar dataKey="purchasedLicenses" fill="#f59e0b" radius={[6, 6, 0, 0]} name="Purchased Licenses" />
                          <Bar dataKey="usedLicenses" fill="#16a34a" radius={[6, 6, 0, 0]} name="Used Licenses" />
                          <Bar dataKey="remainingLicenses" fill="#2563eb" radius={[6, 6, 0, 0]} name="Remaining Licenses" />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  ) : (
                    <EmptyState title="No license data" desc="No visible license rows available." />
                  )}
                </Paper>
              </Box>

              <ScrollableTableContainer
                showScrollButtons={false}
                sx={(t) => ({
                  border: `1px solid ${alpha(t.palette.divider, 0.7)}`,
                  borderRadius: 2,
                  overflow: "hidden",
                  "& table": { minWidth: 520 },
                })}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow sx={(t) => ({ bgcolor: alpha(t.palette.info.main, 0.06) })}>
                      <TableCell sx={{ fontWeight: 900 }}>User</TableCell>
                      <TableCell sx={{ fontWeight: 900 }}>Role</TableCell>
                      <TableCell sx={{ fontWeight: 900 }}>Created By</TableCell>
                      <TableCell sx={{ fontWeight: 900 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 900 }}>Last Seen</TableCell>
                      <TableCell sx={{ fontWeight: 900 }}>Created At</TableCell>
                      <TableCell sx={{ fontWeight: 900 }} align="right">Purchased</TableCell>
                      <TableCell sx={{ fontWeight: 900 }} align="right">Used</TableCell>
                      <TableCell sx={{ fontWeight: 900 }} align="right">Remaining</TableCell>
                      <TableCell sx={{ fontWeight: 900 }}>Last Login</TableCell>
                      <TableCell sx={{ fontWeight: 900 }}>Last Activity</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {licenseTableRows.slice(0, 10).map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell sx={{ fontWeight: 800 }}>{row.name}</TableCell>
                        <TableCell>{row.roleLabel}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{row.createdByName}</TableCell>
                        <TableCell sx={{ fontWeight: 800, color: row.isOnline ? "#16a34a" : "#6b7280" }}>
                          {formatPresenceStatus(row.isOnline)}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {formatRelativeTime(row.lastSeenAt)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDT(row.lastSeenAt)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {formatRelativeTime(row.createdAt)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDT(row.createdAt)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800 }}>{formatCount(row.purchasedLicenses)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800 }}>{formatCount(row.usedLicenses)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800, color: "primary.main" }}>{formatCount(row.remainingLicenses)}</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {formatRelativeTime(row.lastLoginAt)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDT(row.lastLoginAt)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {formatRelativeTime(row.lastActivityAt)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDT(row.lastActivityAt)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                    {!licenseTableRows.length && (
                      <TableRow>
                        <TableCell colSpan={11}>
                          <Typography variant="body2" color="text.secondary">
                            No license data in your visible scope.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </ScrollableTableContainer>
            </Box>
          </Paper>
        </Box>
      )}

      {/* ══ 7. SUPER ADMIN ONLY — License + Inventory ══ */}
      {isSuper && (
        <Box sx={{ px: { xs: 1, sm: 2 }, pb: 3, display: "flex", flexDirection: "column", gap: 2 }}>
          {/* License Expiry */}
          <Paper elevation={2} sx={{ borderRadius: 2 }}>
            <Box
              sx={{
                px: 2, py: 1.5,
                borderBottom: "1px solid", borderColor: "divider",
                display: "flex", alignItems: "center", justifyContent: "space-between",
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                License Expiry (Next 30 Days)
              </Typography>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                {licenseKpis.expiringSoon > 0 && (
                  <Chip size="small" color="warning" label={`${licenseKpis.expiringSoon} expiring`} />
                )}
                {licenseKpis.expiringList.length > 0 && (
                  <Tooltip title="Download CSV">
                    <IconButton
                      size="small"
                      onClick={() =>
                        exportDatasetAsCsv({
                          fileName: "license-expiry-next-30-days",
                          columns: [
                            { key: "title", label: "Device" },
                            { key: "vehicleNumber", label: "Vehicle Number" },
                            { key: "imei", label: "IMEI" },
                            { key: "__exp", label: "Expiry Date", value: (row) => formatDT(row.__exp) },
                            { key: "__days", label: "Days Left" },
                          ],
                          rows: licenseKpis.expiringList,
                        })
                      }
                    >
                      <DownloadRoundedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Refresh">
                  <IconButton size="small" onClick={refetchDevices}>
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            <Box sx={{ p: 2 }}>
              {devicesLoading ? (
                <Skeleton variant="rounded" height={150} />
              ) : licenseKpis.topExpiring.length ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {licenseKpis.topExpiring.map((l, idx) => (
                    <Box
                      key={l?._id || idx}
                      sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 900 }} noWrap>
                          {l?.title || l?.name || l?.type || "License"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Expires: {formatDT(l.__exp)} • In {l.__days} days
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        color={l.__days <= 7 ? "error" : "warning"}
                        label={`${l.__days}d`}
                      />
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  No licenses expiring in next 30 days.
                </Typography>
              )}
            </Box>
          </Paper>

          {/* Inventory Activity */}
          {invTxns.length > 0 && (
            <Paper elevation={2} sx={{ borderRadius: 2 }}>
              <Box
                sx={{
                  px: 2, py: 1.5,
                  borderBottom: "1px solid", borderColor: "divider",
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  Inventory Activity
                </Typography>
                <Tooltip title="Refresh">
                  <IconButton size="small" onClick={() => { refetchInv(); refetchInvTxns(); }}>
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
                {invTxns.slice(0, 6).map((tx, idx) => (
                  <Box
                    key={tx?._id || idx}
                    sx={{ display: "flex", justifyContent: "space-between", gap: 1, minWidth: 0 }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 800 }} noWrap>
                      {tx?.action || tx?.type || "Txn"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                      {formatDT(tx?.createdAt || tx?.date)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          )}
        </Box>
      )}

      <Menu
        anchorEl={exportMenuAnchorEl}
        open={Boolean(exportMenuAnchorEl)}
        onClose={closeExportMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            minWidth: 180,
            borderRadius: 2,
          },
        }}
      >
        {safeArr(activeExportConfig?.formats || ["csv", "xlsx", "pdf"]).map((format) => (
          <MenuItem key={format} onClick={() => handleExportSelection(format)}>
            {`Download ${String(format).toUpperCase()}`}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}
